import { create } from 'zustand';
import {
  applyTheme,
  readStoredTheme,
  readStoredBorder,
  readStoredWidth,
  readStoredMotion,
  readStoredRadius,
  setBorderLevel,
  setWidthLevel,
  setMotionLevel,
  setRadiusLevel,
  readStoredDensity,
  setDensityLevel,
  readStoredLanding,
  setLandingLevel,
  readStoredMenuExpand,
  setMenuExpandLevel,
  readStoredMenuPinned,
  // 与 store 字段同名：别名导入，避免 store 定义里 `setMenuPinned` 自指
  setMenuPinned as setMenuPinnedStorage,
  readStoredTimeFmt,
  setTimeFmtLevel,
} from '../theme/applyTheme';
import { readStoredRingEnabled, readStoredRingVolume, setRingEnabledLevel, setRingVolumeLevel } from '../utils/ringPrefs';

/**
 * 界面设置状态：主题键 + 界面偏好（线框 / 内容宽度 / 动效 / 圆角 / 表格密度 / 登录落点 / 侧栏展开 / 时间显示）
 * + 提醒偏好（来电铃声开关 / 提醒音量）+ 抽屉开合。
 * 视觉与界面偏好的值来源与持久化在 theme/applyTheme.ts（localStorage 键 `bcc.theme` / `bcc.border` /
 * `bcc.width` / `bcc.motion` / `bcc.radius` / `bcc.density` / `bcc.landing` / `bcc.menu.*` / `bcc.timefmt`），
 * 提醒偏好在 utils/ringPrefs.ts（`bcc.ring.*`）；本 store 只负责让抽屉等 UI 拿到响应式的当前值。
 * 抽屉实例只挂一处（BasicLayout），侧栏底栏入口与其它入口统一调 `openDrawer()`。
 */
interface ThemeState {
  key: string;
  setTheme: (key: string) => void;
  /** 线框档位（theme/borders.ts 的 key：soft / standard / strong） */
  border: string;
  setBorder: (key: string) => void;
  /** 内容宽度档（theme/uiPrefs.ts：full / w1440 / w1280） */
  width: string;
  setWidth: (key: string) => void;
  /** 动效档（standard / reduced / system） */
  motion: string;
  setMotion: (key: string) => void;
  /** 圆角档（theme / sharp / standard / round） */
  radius: string;
  setRadius: (key: string) => void;
  /** 表格密度档（loose / standard / compact） */
  density: string;
  setDensity: (key: string) => void;
  /** 登录落点档（dashboard / workbench / inbox）：不参与视觉下发，只影响登录后跳哪一页 */
  landing: string;
  setLanding: (key: string) => void;
  /** 侧栏分组默认展开档（current / pinned / all）：只影响侧栏，不参与视觉下发 */
  menuExpand: string;
  setMenuExpand: (key: string) => void;
  /** 「常开分组」勾选的域键（`pinned` 档使用） */
  menuPinned: string[];
  setMenuPinned: (keys: string[]) => void;
  /** 时间显示档（absolute / relative）：只影响展示，导出恒为绝对值 */
  timeFmt: string;
  setTimeFmt: (key: string) => void;
  /** 来电铃声开关（on / off）：关 = 只停振铃音，标题闪烁与系统通知保留 */
  ringEnabled: string;
  setRingEnabled: (key: string) => void;
  /** 提醒音量档（soft / standard / loud）：作用于来电振铃与高风险提醒的合成提示音 */
  ringVolume: string;
  setRingVolume: (key: string) => void;
  /** 界面设置抽屉可见性（单一实例，由 BasicLayout 挂载） */
  drawerVisible: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
}

export const useThemeStore = create<ThemeState>((set) => ({
  key: readStoredTheme(),
  setTheme: (key: string) => {
    applyTheme(key);
    set({ key });
  },
  border: readStoredBorder(),
  setBorder: (key: string) => {
    setBorderLevel(key);
    set({ border: key });
  },
  width: readStoredWidth(),
  setWidth: (key: string) => {
    setWidthLevel(key);
    set({ width: key });
  },
  motion: readStoredMotion(),
  setMotion: (key: string) => {
    setMotionLevel(key);
    set({ motion: key });
  },
  radius: readStoredRadius(),
  setRadius: (key: string) => {
    setRadiusLevel(key);
    set({ radius: key });
  },
  density: readStoredDensity(),
  setDensity: (key: string) => {
    setDensityLevel(key);
    set({ density: key });
  },
  landing: readStoredLanding(),
  setLanding: (key: string) => {
    setLandingLevel(key);
    set({ landing: key });
  },
  menuExpand: readStoredMenuExpand(),
  setMenuExpand: (key: string) => {
    setMenuExpandLevel(key);
    set({ menuExpand: key });
  },
  menuPinned: readStoredMenuPinned(),
  setMenuPinned: (keys: string[]) => {
    setMenuPinnedStorage(keys);
    set({ menuPinned: keys });
  },
  timeFmt: readStoredTimeFmt(),
  setTimeFmt: (key: string) => {
    setTimeFmtLevel(key);
    set({ timeFmt: key });
  },
  ringEnabled: readStoredRingEnabled(),
  setRingEnabled: (key: string) => {
    setRingEnabledLevel(key);
    set({ ringEnabled: key });
  },
  ringVolume: readStoredRingVolume(),
  setRingVolume: (key: string) => {
    setRingVolumeLevel(key);
    set({ ringVolume: key });
  },
  drawerVisible: false,
  openDrawer: () => set({ drawerVisible: true }),
  closeDrawer: () => set({ drawerVisible: false }),
}));
